import { type PaletteName, PX, ramp } from "./palette";

/**
 * Sprites are authored as text: one character per pixel, mapped to a colour
 * by a legend. `.` and spaces are transparent. The default legend covers the
 * outline ink and the steel ramp; sprites add their own keys on top.
 *
 * The keys `A` (base), `a` (shadow), `Q` (deep) and `S` (shine) are accent
 * slots. They are filled from `ramp(accent)` at render time so one sprite can
 * be painted in any station, section or lane colour.
 */
export type Legend = Record<string, PaletteName | `#${string}` | "transparent">;

export interface SpriteDef {
  readonly w: number;
  readonly h: number;
  readonly rows: readonly string[];
  readonly legend: Legend;
  readonly name?: string;
}

const DEFAULT_LEGEND: Legend = {
  k: "ink",
  "0": "steel950",
  "1": "steel900",
  "2": "steel850",
  "3": "steel800",
  "4": "steel700",
  "5": "steel600",
  "6": "steel500",
  "7": "steel400",
  "8": "steel300",
  "9": "steel200",
  w: "steel50",
  W: "white",
};

const ACCENT_KEYS = new Set(["A", "a", "Q", "S"]);
const DEFAULT_ACCENT = PX.cyan;

/** Parse a pixel map. Leading/trailing blank lines are ignored; rows are right-padded. */
export function sprite(art: string, legend: Legend = {}, name?: string): SpriteDef {
  const lines = art.replace(/\r/g, "").split("\n");
  while (lines.length && !lines[0]!.trim()) lines.shift();
  while (lines.length && !lines[lines.length - 1]!.trim()) lines.pop();
  const indent = Math.min(...lines.filter((l) => l.trim()).map((l) => l.match(/^ */)![0].length));
  const rows = lines.map((l) => l.slice(indent).replace(/\s+$/, ""));
  const w = Math.max(...rows.map((r) => r.length));
  if (import.meta.env?.DEV !== false && rows.some((r) => r.length !== w))
    console.warn(`sprite ${name ?? "?"} has ragged rows`, rows.map((r) => r.length).join(","));
  return { w, h: rows.length, rows: rows.map((r) => r.padEnd(w, ".")), legend, name };
}

/** Mirror a sprite horizontally (cars and workers facing the other way). */
export function flipX(def: SpriteDef): SpriteDef {
  return { ...def, rows: def.rows.map((r) => r.split("").reverse().join("")) };
}

export function flipY(def: SpriteDef): SpriteDef {
  return { ...def, rows: [...def.rows].reverse() };
}

export interface RenderOptions {
  accent?: string;
  /** Extra per-render colour overrides, e.g. to swap a lamp colour. */
  overrides?: Legend;
}

function resolve(value: string): string | null {
  if (value === "transparent") return null;
  return value.startsWith("#") ? value : (PX[value as PaletteName] ?? "#FF00FF");
}

function colourFor(def: SpriteDef, key: string, options: RenderOptions): string | null {
  if (key === "." || key === " ") return null;
  const override = options.overrides?.[key];
  if (override) return resolve(override);
  if (ACCENT_KEYS.has(key) && !def.legend[key]) {
    const r = ramp(options.accent ?? DEFAULT_ACCENT);
    return key === "A" ? r.base : key === "a" ? r.shadow : key === "Q" ? r.deep : r.shine;
  }
  const value = def.legend[key] ?? DEFAULT_LEGEND[key];
  return value ? resolve(value) : "#FF00FF";
}

/** Resolved colours for every pixel (null = transparent). DOM-free, so tooling can rasterise sprites too. */
export function spritePixels(def: SpriteDef, options: RenderOptions = {}): (string | null)[][] {
  return def.rows.map((row) => row.split("").map((key) => colourFor(def, key, options)));
}

type Canvas = HTMLCanvasElement;
const cache = new WeakMap<SpriteDef, Map<string, Canvas>>();

function cacheKey(options: RenderOptions, scale: number): string {
  return `${options.accent ?? ""}|${options.overrides ? JSON.stringify(options.overrides) : ""}|${scale}`;
}

/** Render a sprite to a (cached) canvas at an integer scale. */
export function renderSprite(def: SpriteDef, options: RenderOptions = {}, scale = 1): Canvas {
  let byKey = cache.get(def);
  if (!byKey) {
    byKey = new Map();
    cache.set(def, byKey);
  }
  const key = cacheKey(options, scale);
  const hit = byKey.get(key);
  if (hit) return hit;
  const canvas = document.createElement("canvas");
  canvas.width = def.w * scale;
  canvas.height = def.h * scale;
  const ctx = canvas.getContext("2d")!;
  spritePixels(def, options).forEach((row, y) => {
    row.forEach((colour, x) => {
      if (!colour) return;
      ctx.fillStyle = colour;
      ctx.fillRect(x * scale, y * scale, scale, scale);
    });
  });
  byKey.set(key, canvas);
  return canvas;
}

const urlCache = new WeakMap<SpriteDef, Map<string, string>>();

/** PNG data URL for DOM use (`<img>`, CSS backgrounds, border images, cursors). */
export function spriteUrl(def: SpriteDef, options: RenderOptions = {}, scale = 1): string {
  let byKey = urlCache.get(def);
  if (!byKey) {
    byKey = new Map();
    urlCache.set(def, byKey);
  }
  const key = cacheKey(options, scale);
  const hit = byKey.get(key);
  if (hit) return hit;
  const url = renderSprite(def, options, scale).toDataURL("image/png");
  byKey.set(key, url);
  return url;
}

/** Draw a sprite into a world context at integer coordinates. */
export function draw(
  ctx: CanvasRenderingContext2D,
  def: SpriteDef,
  x: number,
  y: number,
  options: RenderOptions = {},
): void {
  ctx.drawImage(renderSprite(def, options), Math.round(x), Math.round(y));
}
